import React, { Component } from 'react';
import axios from 'axios';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import { parse } from 'qs';
import i18next from 'i18next';
import store from '../../store';
import { GetEmailSpecTemplate } from './EmailSpecTemplate';
import Status from './Status';
import { Envelope } from '../icons';
import Copy from './copy';

// Safely get locale and copy with error handling
let language, type, locale, copy;
try {
  const state = store.getState();
  language = (state && state.content && state.content.language) ? state.content.language : 'en-gb';
  const queryString = window.location.search ? window.location.search.replace('?', '') : '';
  const parsed = parse(queryString);
  type = parsed && parsed.type ? parsed.type : null;
  locale = `${language.replace('-', '_')}${type ? '_' + type : ''}`;
  // Fallback to 'en_gb' or 'en' if locale doesn't exist to prevent crashes
  copy = Copy[locale] || Copy['en_gb'] || Copy['en'] || {};

  // Final safety check - ensure copy has required structure
  if (!copy.inputs || !copy.inputs.email) {
    console.warn('EmailForm: Copy structure incomplete, using fallback');
    copy = Copy['en_gb'] || Copy['en'] || {};
  }
} catch (error) {
  console.error('EmailForm: Error initializing copy data:', error);
  // Use safe fallback
  copy = Copy['en_gb'] || Copy['en'] || {};
  locale = 'en_gb';
}

// Get dynamic asset path for email images based on current domain
const getAssetPath = () => {
  const origin = window.location.origin;

  // If running on localhost, use the deployed dev frontend URL
  // Email clients can't access localhost URLs
  if (origin.includes('localhost')) {
    return 'https://dev-selector.eame.mobil.com/';
  }

  // For staging/production, use the current domain
  return `${origin}/`;
};

// Form to be wrapped into Formik below
class Form extends Component {
  render() {
    const {
      values,
      touched,
      errors,
      dirty,
      isSubmitting,
      handleChange,
      handleBlur,
      handleSubmit,
      status,
      disabled,
    } = this.props;
    // Safety check to prevent crashes if copy is not properly loaded
    if (!copy || !copy.inputs || !copy.inputs.email) {
      console.error('EmailForm: Copy data not loaded for locale:', locale);
      return null;
    }

    const isMissingError = errors.email === copy.inputs.email.error.missing;
    const hasActualError = (errors.email && touched.email && !isMissingError) || status === 'failed';

    return (
      <form className="email-form" onSubmit={handleSubmit}>
        {status && copy.messages && (
          <Status status={status}>
            {/* {status === 'sending' && <img src='../images/loader.gif' alt='...' />} */}
            {copy.messages[status] || 'Processing...'}
          </Status>
        )}
        <div className="email-form__content">
          <h2 className="email-form__title">{copy.title || 'Share your engine oil recommendation'}</h2>
          <div className="email-form__cta-wrapper">
            <div className="input__wrapper">
              <div className={`email-form__field ${hasActualError ? 'email-form__field--error' : ''}`}>
                <div className="email-form__field__icon">
                  <Envelope size="32px" />
                </div>
                <input
                  id="email"
                  autoComplete="email"
                  placeholder={copy.inputs.email.placeholder || copy.inputs.email.label}
                  type="text"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              {hasActualError && errors.email && (
                <div className="email-form__help-group">
                  <div className="feedback">{errors.email}</div>
                </div>
              )}
            </div>
            <button
              className="button"
              type="submit"
              disabled={
                isSubmitting || errors.name || errors.email || errors.cc_email || !dirty || disabled
              }
            >
              {copy.cta || 'Send'}
            </button>
          </div>
        </div>
      </form>
    );
  }
}

const EmailForm = withFormik({
  mapPropsToValues: (props) => ({
    email: (copy && copy.inputs && copy.inputs.email) ? copy.inputs.email.default : '',
    onFormSubmit: props.onFormSubmit,
    copy: copy && copy.email ? copy.email : {},
    basket: props.basket,
  }),
  validationSchema: Yup.lazy(() => {
    // Safety check for copy data
    if (!copy || !copy.inputs || !copy.inputs.email) {
      return Yup.object().shape({
        email: Yup.string()
          .email('Please enter a valid e-mail address')
          .required('Please provide an email address'),
      });
    }
    return Yup.object().shape({
      email: Yup.string()
        .email(copy.inputs.email.error.invalid)
        .required(copy.inputs.email.error.missing),
    });
  }),
  handleSubmit: (values, { setSubmitting, setStatus, props }) => {
    const { selectedFamily } = props;
    const state = store.getState().content;
    const equipment = state.results;
    const registration = state.reg || '';

    // Helper function to get product data by GPN
    const getProductData = (gpn) => {
      return equipment.products.find((product) => product.gpn === gpn) || {};
    };

    // Translation function that uses the translations namespace
    const translate = (key, options) => {
      return i18next.t(key, { ...options, ns: 'translations' });
    };

    // Safety check for copy data before generating email
    if (!copy || !copy.email) {
      setSubmitting(false);
      setStatus('failed');
      console.error('EmailForm: Email copy data not available');
      return;
    }

    const queryString = window.location.search ? window.location.search.replace('?', '') : '';
    const query = parse(queryString);

    let emailBody;
    try {
      emailBody = GetEmailSpecTemplate({
        equipment,
        copy: { ...copy.email, asset_path: getAssetPath() },
        t: translate,
        registration,
        getProductData,
        selectedFamily,
        query,
      });

      // Validate that emailBody is a string
      if (typeof emailBody !== 'string') {
        throw new Error('Email body is not a valid HTML string');
      }
    } catch (error) {
      setSubmitting(false);
      setStatus('failed');
      console.error('EmailForm: Error generating email HTML:', error);
      return;
    }

    setStatus('sending');
    axios
      .request({
        url: 'https://emailfolioapi.dps.mobil.com/sendemail',
        method: 'post',
        headers: {
          'content-type': 'application/json',
        },
        data: {
          FromText: copy.email.from_text || 'ExxonMobil',
          SendTo: values.email,
          SendCc: '',
          Subject: copy.email.subject || 'Your Mobil™ product recommendation',
          EmailBody: emailBody,
          IsHtml: true, // Indicate that EmailBody contains HTML content
          PublicKey: '352dc81e-70d8-4720-96a8-2078148426bc',
        },
      })
      .then(() => {
        setSubmitting(false);
        setStatus('sent');
      })
      .catch((error) => {
        setSubmitting(false);
        setStatus('failed');
        console.error('EmailForm: Error sending email:', error);
        if (error.response) {
          console.error('Response data:', error.response.data);
          console.error('Response status:', error.response.status);
        }
      });
  },
  displayName: 'EmailForm', // helps with React DevTools
})(Form);

export default EmailForm;
